import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import isEmailValid from '../../../../../utils/isEmailValid';
import { EmailsObjectType } from '../../types';

export default function useFinancialEmails() {
  const [addFinancialEmailModalShow, setAddFinancialEmailModalShow] = useState(false);
  const [removeFinancialEmailModalShow, setRemoveFinancialEmailModalShow] = useState(false);
  const [financialEmails, setFinancialEmails] = useState<EmailsObjectType[]>([]);
  const [financialEmailToAdd, setFinancialEmailToAdd] = useState('');
  const [financialEmailToRemove, setFinancialEmailToRemove] = useState('');
  const [originalFinancialEmails, setOriginalFinancialEmails] = useState<EmailsObjectType[]>([]);
  const [financialEmailOwnerNameToAdd, setFinancialEmailOwnerNameToAdd] = useState('');

  const hasOriginalFinancialEmailsChanged = useMemo(
    () => JSON.stringify(originalFinancialEmails) !== JSON.stringify(financialEmails),
    [originalFinancialEmails, financialEmails],
  );
  const hasFinancialEmailsToBeRendered = useMemo(
    () => financialEmails.length > 0,
    [financialEmails],
  );

  function handleFinancialEmailToAddChange(event: ChangeEvent<HTMLInputElement>) {
    setFinancialEmailToAdd(event.target.value);
  }

  function handleFinancialEmailOwnerNameToAddChange(event: ChangeEvent<HTMLInputElement>) {
    setFinancialEmailOwnerNameToAdd(event.target.value);
  }

  const addFinancialEmail = useCallback(() => {
    const onlyEmails = financialEmails.map((emailObj) => emailObj.email);
    const doesEmailAlreadyExists = onlyEmails.includes(financialEmailToAdd);
    if (doesEmailAlreadyExists) {
      toast.warn('Este e-mail já foi adicionado!');
      return;
    }
    if (!isEmailValid(financialEmailToAdd)) {
      toast.warn('E-mail inválido!');
      return;
    }
    setFinancialEmails((prevState) => [
      ...prevState,
      { email: financialEmailToAdd, emailOwnerName: financialEmailOwnerNameToAdd },
    ]);
    setFinancialEmailToAdd('');
    setFinancialEmailOwnerNameToAdd('');
    toast.success('E-mail adicionado. Não se esqueça de salvar as alterações para confirmar');
  }, [financialEmailOwnerNameToAdd, financialEmailToAdd, financialEmails]);

  const removeFinancialEmail = useCallback(() => {
    const filteredEmails = financialEmails.filter(
      (email) => email.email !== financialEmailToRemove,
    );
    setFinancialEmails(filteredEmails);
    setRemoveFinancialEmailModalShow(false);
    toast.success('E-mail removido. Não esqueça de salvar as configurações para aplicar as mudanças!');
    setFinancialEmailToRemove('');
  }, [financialEmailToRemove, financialEmails]);

  const resetFinancialEmails = useCallback(() => {
    setFinancialEmails(originalFinancialEmails);
    toast.info('Suas alterações foram desfeitas');
  }, [originalFinancialEmails]);

  return {
    setAddFinancialEmailModalShow,
    hasFinancialEmailsToBeRendered,
    financialEmails,
    setFinancialEmails,
    setFinancialEmailToRemove,
    setRemoveFinancialEmailModalShow,
    financialEmailToRemove,
    removeFinancialEmail,
    addFinancialEmailModalShow,
    financialEmailToAdd,
    handleFinancialEmailToAddChange,
    addFinancialEmail,
    removeFinancialEmailModalShow,
    resetFinancialEmails,
    hasOriginalFinancialEmailsChanged,
    setOriginalFinancialEmails,
    financialEmailOwnerNameToAdd,
    handleFinancialEmailOwnerNameToAddChange,
  };
}
